import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { useHistory } from 'react-router-dom'

import OverflowToolbarPanel, {
    HammockToolbarButtonData,
    HammockToolbarProps,
    useOverflowToolbarPanelStyles,
} from '../../../components/Overflow/OverflowToolbarPanel'
import { generatePath } from 'react-router'
import { fileRoutePrefix } from '../../../hooks/file/useFilePages'
import { ClipboardTaskIcon, FlowchartIcon, MailIcon, PeopleIcon, TextBulletListTreeIcon, TimerIcon } from '../../../components/MainMenu'
import {
    BrainCircuitFilled,
    BrainCircuitRegular,
    BriefcaseMedicalFilled,
    BriefcaseMedicalRegular,
    bundleIcon,
    ChatHelpFilled,
    ChatHelpRegular,
    DocumentLandscapeDataFilled,
    DocumentLandscapeDataRegular,
    DocumentLinkFilled,
    DocumentLinkRegular,
    DocumentLockFilled,
    DocumentLockRegular,
    DocumentMultipleFilled,
    DocumentMultipleRegular,
    GiftCardFilled,
    GiftCardRegular,
    HeartBrokenFilled,
    HeartBrokenRegular,
    NotebookFilled,
    NotebookRegular,
    NoteFilled,
    NoteRegular,
    PaymentFilled,
    PaymentRegular,
    TargetEditFilled,
    TargetEditRegular,
    VehicleCarProfileFilled,
    VehicleCarProfileRegular,
} from '@fluentui/react-icons'
import { AIAgentEnum, ModuleType } from '../../../context/GlobalContext'
import useRoles, { Role, rolesTasks, rolesTimes } from '../../../hooks/useRoles'
import useModules from '../../../hooks/module/useModules'
import { usePageStyles, ViewFileProps } from './ViewFile'
import { useFileModules } from '../../../hooks/module/useFileModule'
import { Card } from '@fluentui/react-components'
import { useSettings } from '../../../hooks/useSettings'
import useAttributes, { AttributeContextType, AttributeViewType, EntityAttributeType } from '../../../hooks/useAttributes'
import { useTranslation } from 'react-i18next'
import { LegalPAIcon } from '../../../components/Icon/LegalPAIcon'

const AIAgentIcon = bundleIcon(BrainCircuitFilled, BrainCircuitRegular)
const AuditTrailIcon = bundleIcon(DocumentLockFilled, DocumentLockRegular)
const DisbursementIcon = bundleIcon(PaymentFilled, PaymentRegular)
const DocumentIcon = bundleIcon(DocumentMultipleFilled, DocumentMultipleRegular)
const DocumentSharedIcon = bundleIcon(DocumentLinkFilled, DocumentLinkRegular)
const FixedPriceIcon = bundleIcon(GiftCardFilled, GiftCardRegular)
const LegalAidCertificateIcon = bundleIcon(ChatHelpFilled, ChatHelpRegular)
const DocketIcon = bundleIcon(NotebookFilled, NotebookRegular)
const InjuryIcon = bundleIcon(BriefcaseMedicalFilled, BriefcaseMedicalRegular)
const TravelIcon = bundleIcon(VehicleCarProfileFilled, VehicleCarProfileRegular)
const NoteIcon = bundleIcon(NoteFilled, NoteRegular)
const PersonLawIcon = bundleIcon(HeartBrokenFilled, HeartBrokenRegular)
const RegistrationIcon = bundleIcon(TargetEditFilled, TargetEditRegular)
const MonitoringIcon = bundleIcon(DocumentLandscapeDataFilled, DocumentLandscapeDataRegular)

export enum ToolbarItemKey {
    Party = 'party',
    Email = 'email',
    Document = 'document',
    SharedDocument = 'shareddocument',
    Task = 'task',
    Workflow = 'workflow',
    Time = 'time',
    Disbursement = 'disbursement',
    FixedPrice = 'fixed-price',
    LegalAidCertificate = 'legal-aid-certificate',
    LegalPa = 'legalpa',
    Docket = 'docket',
    Injury = 'injury',
    Travel = 'travel',
    Note = 'note',
    FamilyLaw = 'family-law',
    Attribute = 'attribute',
    Monitoring = 'monitoring',
    AIAgent = 'ai-agent',
    AuditTrail = 'audit-trail',
}

// Tab value and route segment for an attribute category; the <Route> paths in ViewFile must use the same slug.
export function attributeCategoryTabKey(category: string): string {
    return `${ToolbarItemKey.Attribute}-${category.toLowerCase().replaceAll(/[_\s]+/g, '-')}`
}

export default function ViewFileTabs(props: ViewFileProps): ReactElement {
    const { attributeCategories } = useAttributes(
        AttributeViewType.View,
        [AttributeContextType.File],
        undefined,
        EntityAttributeType.File,
        props.file,
    )
    const history = useHistory()
    const modules = useModules()
    const roles = useRoles()
    const { settings } = useSettings()
    const { t: translate } = useTranslation()

    const pageStyles = usePageStyles()
    const toolbarStyles = useOverflowToolbarPanelStyles()

    const fileModules = useFileModules(props.file)

    const toolbarProps = useMemo<HammockToolbarProps>(
        (): HammockToolbarProps => ({
            className: pageStyles.toolbar,
        }),
        [],
    )

    const activeButton = useMemo<ToolbarItemKey>(() => {
        return props.routeMatch.params?.tab || ToolbarItemKey.Document
    }, [props.routeMatch])

    const onSelectButton = (value?: string) => {
        const button: HammockToolbarButtonData = buttons
            .flatMap(button => [button, ...(button.childButtons || [])])
            .find(button => (button.value as string) === value)
        if (button?.route) {
            if (props.selectedDriveItemIds.length > 0) {
                props.setSelectedDriveItemIds([])
            }
            if (props.selectedDriveItems.length > 0) {
                props.setSelectedDriveItems([])
            }
            history.push(generatePath(fileRoutePrefix + button?.route, props.routeMatch.params))
        }
    }

    function getAIAgentIcon(agent: AIAgentEnum): React.JSX.Element {
        if (agent === AIAgentEnum.LegalPa) {
            return <LegalPAIcon />
        }

        return <AIAgentIcon />
    }

    const aiAgentButtons = useMemo<HammockToolbarButtonData[]>(
        () =>
            Object.values(AIAgentEnum)
                .map((agent): HammockToolbarButtonData => {
                    const agentKey = agent.toLowerCase().replaceAll('_', '-')

                    return {
                        value: ToolbarItemKey.AIAgent,
                        icon: getAIAgentIcon(agent),
                        titleKey: `file.pivot.aiagent.${agentKey}.title`,
                        route: `/ai-agent/${agentKey}`,
                        filter: () => modules.has(ModuleType.AIAgent) && settings.aiAgent?.enabled && settings.aiAgent?.agents?.includes(agent),
                        onClick: onSelectButton,
                    }
                })
                .filter(agent => agent.filter(agent)),
        [modules, settings],
    )

    const registrationButtons = useMemo<HammockToolbarButtonData[]>(
        () =>
            [
                {
                    value: ToolbarItemKey.Time,
                    icon: <TimerIcon />,
                    titleKey: 'file.pivot.time.title',
                    route: '/time',
                    filter: () => modules.has(ModuleType.TimeTracking) && roles.any(rolesTimes),
                    onClick: onSelectButton,
                },
                {
                    value: ToolbarItemKey.Travel,
                    icon: <TravelIcon />,
                    titleKey: 'file.pivot.travel.title',
                    route: '/travel',
                    filter: () => modules.has(ModuleType.TravelExpenses),
                    onClick: onSelectButton,
                },
                {
                    value: ToolbarItemKey.Disbursement,
                    icon: <DisbursementIcon />,
                    titleKey: 'file.pivot.disbursement.title',
                    route: '/disbursement',
                    filter: () => modules.has(ModuleType.Disbursements),
                    onClick: onSelectButton,
                },
                {
                    value: ToolbarItemKey.FixedPrice,
                    icon: <FixedPriceIcon />,
                    titleKey: 'file.pivot.fixedprice.title',
                    route: '/fixed-price',
                    filter: () => modules.has(ModuleType.FixedPrices),
                    onClick: onSelectButton,
                },
                {
                    value: ToolbarItemKey.Injury,
                    icon: <InjuryIcon />,
                    titleKey: 'file.pivot.injury.title',
                    route: '/injury',
                    filter: () => fileModules.has(ModuleType.Injury),
                    onClick: onSelectButton,
                },
                {
                    value: ToolbarItemKey.FamilyLaw,
                    icon: <PersonLawIcon />,
                    titleKey: 'file.pivot.familylaw.title',
                    route: '/family-law',
                    filter: () => fileModules.has(ModuleType.FamilyLaw),
                    onClick: onSelectButton,
                },
                {
                    value: ToolbarItemKey.LegalAidCertificate,
                    icon: <LegalAidCertificateIcon />,
                    titleKey: 'file.pivot.legalaidcertificate.title',
                    route: '/legal-aid-certificate',
                    filter: () => fileModules.has(ModuleType.LegalAid),
                    onClick: onSelectButton,
                },
                {
                    value: ToolbarItemKey.Docket,
                    icon: <DocketIcon />,
                    titleKey: 'file.pivot.docketproceeding.title',
                    route: '/docket',
                    filter: () => fileModules.has(ModuleType.Docket),
                    onClick: onSelectButton,
                },
            ].filter((button: HammockToolbarButtonData) => (button.filter ? button.filter(button) : true)),
        [modules, roles, settings, fileModules],
    )

    const attributeButtons = useMemo<HammockToolbarButtonData[]>(
        () =>
            modules.has(ModuleType.Attributes)
                ? attributeCategories?.map(category => {
                      const key = category || translate('file.pivot.attribute.title')
                      return {
                          value: attributeCategoryTabKey(key),
                          icon: <TextBulletListTreeIcon />,
                          titleKey: 'file.pivot.attribute.title',
                          title: category,
                          route: `/${attributeCategoryTabKey(key)}`,
                          onClick: onSelectButton,
                      }
                  })
                : [],
        [modules, attributeCategories],
    )

    const buttons = useMemo<HammockToolbarButtonData[]>(
        (): HammockToolbarButtonData[] => [
            {
                value: ToolbarItemKey.Party,
                icon: <PeopleIcon />,
                titleKey: 'file.pivot.party.title',
                route: '/party',
                onClick: onSelectButton,
            },
            {
                value: ToolbarItemKey.Email,
                icon: <MailIcon />,
                titleKey: 'file.pivot.email.title',
                route: '/email',
                onClick: onSelectButton,
            },
            {
                value: ToolbarItemKey.Document,
                icon: <DocumentIcon />,
                titleKey: 'file.pivot.document.title',
                route: '/document',
                onClick: onSelectButton,
            },
            {
                value: ToolbarItemKey.SharedDocument,
                icon: <DocumentSharedIcon />,
                titleKey: 'file.pivot.shareddocument.title',
                route: '/document',
                filter: () => false,
                onClick: onSelectButton,
            },
            {
                value: ToolbarItemKey.Task,
                icon: <ClipboardTaskIcon />,
                titleKey: 'file.pivot.task.title',
                route: '/task',
                filter: () => modules.any([ModuleType.Tasks]) && roles.any(rolesTasks),
                onClick: onSelectButton,
            },
            {
                value: ToolbarItemKey.Workflow,
                icon: <FlowchartIcon />,
                titleKey: 'file.pivot.workflow.title',
                route: '/workflow',
                filter: () => modules.any([ModuleType.Workflow]) && roles.any(rolesTasks),
                onClick: onSelectButton,
            },
            {
                value: 'registration',
                icon: <RegistrationIcon />,
                titleKey: 'file.pivot.registration.title',
                filter: () => registrationButtons.length > 0,
                childButtons: registrationButtons,
            },
            {
                value: ToolbarItemKey.Note,
                icon: <NoteIcon />,
                titleKey: 'file.pivot.note.title',
                route: '/note',
                onClick: onSelectButton,
            },
            ...attributeButtons,
            {
                value: ToolbarItemKey.Monitoring,
                icon: <MonitoringIcon />,
                titleKey: 'file.pivot.monitoring.title',
                route: '/monitoring',
                filter: () => modules.has(ModuleType.Invoices) && roles.any([Role.Invoice]),
                onClick: onSelectButton,
            },
            ...aiAgentButtons.filter(() => aiAgentButtons.length === 1),
            {
                value: ToolbarItemKey.AIAgent,
                icon: <AIAgentIcon />,
                titleKey: 'file.pivot.aiagent.title',
                filter: () => modules.has(ModuleType.AIAgent) && aiAgentButtons.length !== 1,
                route: aiAgentButtons.length === 0 ? '/ai-agent' : undefined,
                childButtons: aiAgentButtons,
                onClick: aiAgentButtons.length === 0 ? onSelectButton : undefined,
            },
            {
                value: ToolbarItemKey.AuditTrail,
                icon: <AuditTrailIcon />,
                titleKey: 'file.pivot.audittrail.title',
                route: '/audit-trail',
                filter: () => modules.has(ModuleType.AuditTrail),
                onClick: onSelectButton,
            },
        ],
        [modules, roles, registrationButtons, attributeButtons],
    )

    const activeParentButton = useMemo(
        () =>
            buttons
                .flatMap(button => (button.childButtons || []).map(child => ({ parent: button, value: child })))
                .filter(button => button.value.value === activeButton)
                .map(button => button.parent.value)?.[0],
        [activeButton, buttons],
    )

    return (
        <Card className={pageStyles.toolbarCard}>
            <OverflowToolbarPanel
                containerClassName={pageStyles.toolbarTabs}
                toolbarProps={toolbarProps}
                getToolbarButtonProps={button => ({
                    appearance: activeParentButton === button?.value || activeButton === button?.value ? 'primary' : 'subtle',
                    disabled: button?.disabled,
                })}
                getMenuItemProps={button => ({
                    className: activeButton === button?.value && toolbarStyles.selectedMenuItem,
                    disabled: button?.disabled,
                })}
                buttons={buttons}
                filter={buttons => buttons?.filter((button: HammockToolbarButtonData) => (button.filter ? button.filter(button) : true))}
                overflowButtonLabelKey={'base.item.label'}
            />
        </Card>
    )
}
